import { useCartContext } from '../Contexts/CartContext';
import { useCheckoutContext } from '../Contexts/CheckoutContext';
import { useLayoutContext } from '../Contexts/LayoutContext';
import { useLoginContext } from '../Contexts/LoginContext';
import { useOrderTypeContext } from '../Contexts/OrderTypeContext';
import { useStoreContext } from '../Contexts/StoreContext';
import CartService, { RemoveAllProps } from '../Services/CartService';
import CustomerService from '../Services/CustomerService';
import OrderService from '../Services/OrderService';
import { CART_STATUS_FINISHED, ERROR_CART_NOT_FOUND } from '../Utils/Constants/Cart';
import { formatCartId } from '../Utils/Formatters';
import { useProductsHook } from './ProductsHook';
import { useStoreHook } from './StoreHook';

export const useCartHook = () => {

  const { setMainLoading, setMainLoadingIcon, setMainLoadingIconSearchCart, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconNoSearchMap, setVersionFront } = useLayoutContext();
  const { cartDisplay, setCartDisplay, setShowCartIsFinished, setCartFinished, setCartConference, setShowCart, setShowCartConference, setShowConfirmTable, setCurrentCart, getFormattedItemsfromCartConference, setLoadingCurrentCart, setLoadingCoupon, currentCart, setLoadingUpdateQuantity, setLoadingUpdateQuantityProductId } = useCartContext();
  const { currentStore, table, setTable, setTables, setCashier, setRequiredTable } = useStoreContext();
  const { orderType, getOrderTypeIdByName, isStoreCounter } = useOrderTypeContext();
  const { setToken, setUser, setGenerateToken, setGeolocation, token, guestToken, setGuestToken } = useLoginContext();
  const { setShowPixCode, order, setOrder } = useCheckoutContext();
  const { sendProductsStoreCounter } = useProductsHook();
  const { createGuestCart, createGuestToken } = useStoreHook();

  const createCart = async (token: string) => {
    setMainLoading(true);
    setMainLoadingTitle('Abrindo comanda');
    setMainLoadingMessage('Aguarde...');

    const orderTypeId = getOrderTypeIdByName(orderType);
    if (orderTypeId == null) {
      console.error('invalid orderType');
      setMainLoading(false);
      return;
    }

    if (!orderTypeId || !currentStore?.id) {
      console.error('Dados inválidos para criar comanda');
      setMainLoading(false);
      return;
    }

    const payload = {
      store_id: currentStore.id,
      order_type: Number(orderTypeId),
      store_table_id: table,
    };

    try {
      const cartService = new CartService(orderType, token);
      const cartCreated = await cartService.create(payload);

      if (cartCreated) {

        //* forçando seleção de mesa
        if (!table) {
          setShowConfirmTable(true);
          setRequiredTable(true);
        }

        setCartDisplay({
          id: cartCreated.cart.id,
          table: {
            id: table,
            name: cartCreated.cart.table?.name || '',
          },
        });
        setTable(null);
        setCartFinished(null);
        setShowCartIsFinished(false);
        setOrder(null);
      }
    } catch (error: any) {
      if (error.response?.data?.error === 'could_not_refresh_token') {
        setToken(null);
        setUser(null);
        setGenerateToken(null);
      }

      setTimeout(() => {
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingCloseButton(true);
        setMainLoadingTitle('Ops! Algo não deu certo.');
        setMainLoadingMessage('Tente abrir a comanda novamente e, caso persista, chame o garçom.');
        setMainLoading(true);
      }, 1250);

      console.error('Erro ao criar comanda:', error);
    } finally {
      setMainLoading(false);
    }
  };


  interface CartFinishedProps {id: number, order: any}
  const cartFinished = ({id, order}: CartFinishedProps) => {
    setCartFinished({id, order});
    setShowCartIsFinished(true);
    setOrder(null);
    setCartConference([]);
    setCartDisplay(null);
    setCashier(null);
  }

  const getOpenedCustomerCartDiffStored = async (token: string) => {
    try {
      setMainLoading(true);
      setMainLoadingTitle('Verificando sua comanda');
      setMainLoadingMessage('Aguarde...');

      const orderTypeId = getOrderTypeIdByName(orderType);
      if (orderTypeId == null) throw new Error('Tipo de pedido inválido');

      const customerService = new CustomerService(orderType, token);
      const payload = {
        store_id: currentStore?.id,
        order_type: Number(orderTypeId),
      };

      const getCart = await customerService.getCartOpen(payload);

      if (!getCart || !getCart.id) {
        setCartDisplay(null);
      } else if (!cartDisplay || cartDisplay.id !== getCart.id) {
        setCartDisplay({
          id: getCart.id,
          table: {
            id: cartDisplay?.table?.id || table,
            name: getCart.table?.name || '',
          },
        });

        setMainLoadingTitle(`Encontramos sua comanda ${formatCartId(getCart.id)}.`);
        setMainLoadingMessage('Verifique se possui produtos adicionados pelo garçom e continue o seu pedido.');
        setMainLoadingIcon(false);
        setMainLoadingIconSearchCart(true);
        setMainLoadingCloseButton(true);
        setTable(null);
        setCartFinished(null);
      }

      setMainLoading(false);
    } catch (error) {
      setMainLoading(false);
    } finally {
      setMainLoading(false);
    }
  };


  const currentCartNotFound = () => {
    setMainLoadingIcon(false);
    setMainLoadingIconError(true);
    setMainLoading(true);
    setMainLoadingTitle('Não localizamos seu carrinho');
    setMainLoadingMessage('Tente adicionar o produto novamente.');
    setMainLoadingCloseButton(true);
    createGuestToken();
  }

  const cartNotFound = () => {
    setMainLoadingIcon(false);
    setMainLoadingIconError(true);
    setMainLoading(true);
    setMainLoadingTitle(`Sua comanda ${formatCartId(cartDisplay.id)} foi encerrada.`);
    setMainLoadingMessage('Caso queira abrir uma nova comanda, feche esta janela e clique no botão no rodapé ou chame o garçom.');
    setMainLoadingCloseButton(true);
    setCartDisplay(null);
    setShowCart(false);
    setShowCartConference(false);
    setCartConference([]);
    setCashier(null);
    setShowPixCode(false);
    setOrder(null);
  }

  const geolocationBlocked = () => {
    setMainLoadingIcon(false);
    setMainLoadingIconNoSearchMap(true);
    setMainLoadingTitle('Geolocalização negada');
    setMainLoadingMessage('Para abrir a comanda, ativa a localização no navegador e atualiza a página.');
    setMainLoadingCloseButton(true);
    setMainLoading(true);
  }

  const removeLogin = () => {
    setMainLoadingIcon(false);
    setMainLoadingIconError(true);
    setMainLoading(true);
    setMainLoadingTitle('Ops! Sua sessão expirou.');
    setMainLoadingMessage('Para continuar, clique no botão para abrir uma nova comanda, informe seu telefone e valide o token novamente.');
    setMainLoadingCloseButton(true);
    if (cartDisplay) {
      setTable(cartDisplay.table.id);
    }
    setCartDisplay(null);
    setToken(null);
    setUser(null);
  }

  const updatedFrontVersion = () => {
    setCartFinished(null);
    setCartDisplay(null);
    setCashier(null);
    setGeolocation(null);
    setUser(null);
    setToken(null);
    setGenerateToken(null);
    setTable(null);
    setTables(null);
    setVersionFront(process.env.REACT_APP_VERSION);

    //
    setGuestToken(null);
    setCurrentCart(null);
    setOrder(null);
  }

  const cancelPayment = async (cart_id: number, token: string) => {

    setMainLoading(true);
    setMainLoadingTitle('Cancelando pagamento');
    setMainLoadingMessage('Aguarde...');

    const payload = {
      cart_id: cart_id,
    }
    try {
        const orderService = new OrderService(orderType, token);
        const orderCanceled = await orderService.cancelPayment(payload);
        if (orderCanceled.success) {
          setCartDisplay({...cartDisplay, order})
          setShowPixCode(false);
          setOrder(null);
          setMainLoading(false);
        }
        return orderCanceled;
    } catch (error) {
        console.error('Error cancelPayment:', error);
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingTitle('Ops! Algo não deu certo.');
        setMainLoadingMessage(
          isStoreCounter
            ? 'Tente novamente e, caso persista, chame o garçom.'
            : 'Tente novamente em alguns instantes.'
        );
        setMainLoadingCloseButton(true);
        throw error;
    }
  }

  const checkOrdersOpen = async (token: string) => {

    setMainLoading(true);
    setMainLoadingTitle('Verificando seus pedidos');
    setMainLoadingMessage('Aguarde...');

    try {
        const orderService = new OrderService(orderType, token);
        const ordersOpen = await orderService.checkOrdersOpen();
        setMainLoading(false);
        return ordersOpen;
    } catch (error) {
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingTitle('Ops! Algo não deu certo.');
        setMainLoadingMessage('Tente novamente e, caso persista, chame o garçom.');
        setMainLoadingCloseButton(true);
        console.error('Error cancelPayment:', error);
    }
  }

  const applyCouponCode = async (couponCode: string, token: string) => {
    const payload = {
      coupon_code: couponCode,
      order_type: Number(getOrderTypeIdByName(orderType)),
      store_id: currentStore.id,
    }
    try {
        setLoadingCoupon(true);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType, token);
        const couponCodeApplied = await cartService.applyCoupon(cart_id, payload);
        if (couponCodeApplied.success) {
          setCurrentCart(couponCodeApplied.cart);
        } else {
          setMainLoading(true);
          setMainLoadingIcon(false);
          setMainLoadingIconError(true);
          setMainLoadingTitle(couponCodeApplied.message);
          setMainLoadingMessage('Verifique o código digitado e tente novamente.');
          setMainLoadingCloseButton(true);
        }
        setLoadingCoupon(false);
    } catch (error) {
        setLoadingCoupon(false);
    }
  }

  const removeCouponCode = async (couponCode: string, token: string) => {
    const payload = {
      coupon_code: couponCode,
      order_type: Number(getOrderTypeIdByName(orderType)),
      store_id: currentStore.id,
    }
    try {
        setLoadingCoupon(true);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType, token);
        const couponCodeRemoved = await cartService.removeCoupon(cart_id, payload);
        if (couponCodeRemoved.success) {
          setCurrentCart(couponCodeRemoved.cart);
        }
        setLoadingCoupon(false);
    } catch (error) {
        setLoadingCoupon(false);
    }
  }

  const changeQuantityProduct = async (id: number, quantity: number, token: string) => {
    const payload = {
      id: id,
      order_type: Number(getOrderTypeIdByName(orderType)),
      quantity: quantity,
    }
    try {
        setLoadingUpdateQuantity(true);
        setLoadingUpdateQuantityProductId(id);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType, token, guestToken);
        const response = await cartService.changeQuantity(cart_id, payload);
        if (response) {
          setCurrentCart(response);
        }
        setLoadingUpdateQuantity(false);
        setLoadingUpdateQuantityProductId(null);
    } catch (error) {
        setLoadingUpdateQuantity(false);
        setLoadingUpdateQuantityProductId(null);

        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingCloseButton(true);
        setMainLoadingTitle('Ops! Algo não deu certo.');
        setMainLoadingMessage('Tente alterar a quantidade novamente.');
    }
  }

  const deleteProduct = async (id: number, token: string) => {
    const payload = {
      id: id,
      order_type: Number(getOrderTypeIdByName(orderType)),
    }
    try {
        setLoadingUpdateQuantity(true);
        setLoadingUpdateQuantityProductId(id);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType, token, guestToken);
        const response = await cartService.deleteProductFromCart(cart_id, payload);
        if (response) {
          setCurrentCart(response);
        }
        setLoadingUpdateQuantity(false);
        setLoadingUpdateQuantityProductId(null);
    } catch (error) {
      setLoadingUpdateQuantity(false);
      setLoadingUpdateQuantityProductId(null);

      setMainLoading(true);
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingCloseButton(true);
      setMainLoadingTitle('Ops! Algo não deu certo.');
      setMainLoadingMessage('Tente remover o produto novamente.');
    }
  }

  const removeOldCart = () => {
    setCartFinished(null);
    setCartDisplay(null);
    setCashier(null);
    setGeolocation(null);
    setUser(null);
    setToken(null);
    setGenerateToken(null);
    setTable(null);
  }

  const handleSend = () => {
    setShowConfirmTable(false);
    const showCart = async () => {
      const payload = {
          cart_id: cartDisplay.id,
          store_id: currentStore.id,
          order_type: Number(getOrderTypeIdByName(orderType)),
      }
      try {
          setMainLoading(true);
          setMainLoadingIcon(true);
          setMainLoadingTitle('Verificando sua comanda');
          setMainLoadingMessage('Aguarde...');
          const cartService = new CartService(orderType, token);
          const getCart = await cartService.show(payload);

          if (getCart) {
              if (getCart.status === CART_STATUS_FINISHED) {
                  cartFinished({id: cartDisplay.id, order: getCart.order});
                  setMainLoading(false);
                  setShowCartConference(false);
              }
              else {
                  const payload = {
                      apply_tax: true,
                      table_id: cartDisplay.table.id,
                      table_name: cartDisplay.table.name,
                      store_id: currentStore.id,
                      order_type: Number(getOrderTypeIdByName(orderType)),
                      items: getFormattedItemsfromCartConference(cartDisplay.id)
                  }

                  sendProductsStoreCounter(cartDisplay.id, payload).then((success: any) => {
                      if (success) {
                          setShowCartConference(false);
                      }
                  });
              }
          } else {
              setMainLoading(false);
          }
        } catch (error: any) {
            if (error.response?.data?.error === ERROR_CART_NOT_FOUND) {
                cartNotFound();
            } else {
                setMainLoading(false);
            }
        }
    }
    showCart();
  }

  const removeAll = async (cart_id: number, payload: RemoveAllProps) => {
    try {
      setLoadingCurrentCart(true);
      const cartService = new CartService(orderType, token, guestToken);
      return await cartService.removeAll(cart_id, payload)
      .then(response => {
        createGuestCart();
        setShowCart(false);
    }).finally(() => {
      setLoadingCurrentCart(true);
      setMainLoading(false);
    });
    } catch (error) {
      setLoadingCurrentCart(false);

      setMainLoading(true);
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingCloseButton(true);
      setMainLoadingTitle('Ops! Algo não deu certo.');
      setMainLoadingMessage('Ocorreu um erro ao remover os produtos, tente novamente.');
    }
  };

  return {
    createCart,
    cartFinished,
    getOpenedCustomerCartDiffStored,
    cartNotFound,
    currentCartNotFound,
    cancelPayment,
    removeLogin,
    geolocationBlocked,
    removeOldCart,
    updatedFrontVersion,
    handleSend,
    removeAll,
    applyCouponCode,
    removeCouponCode,
    changeQuantityProduct,
    deleteProduct,
    checkOrdersOpen
  };
}
